import React from 'react';

/**
 * Button component props interface
 * Extends HTML button attributes for full compatibility
 */
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant of the button */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  /** Size of the button */
  size?: 'sm' | 'md' | 'lg';
  /** Whether the button is in loading state */
  isLoading?: boolean;
  /** Icon to display on the left side */
  leftIcon?: React.ReactNode;
  /** Icon to display on the right side */
  rightIcon?: React.ReactNode;
  /** Button content */
  children: React.ReactNode;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Additional CSS class names */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
}

/**
 * Design system colors
 */
const COLORS = {
  primary: 'var(--text-primary_med_em)',
  greyBg: 'var(--surface-surface_2)',
  greyBgHover: 'var(--surface-surface_3)',
  greyText: 'var(--text-med_em)',
  danger: '#F04438',
  white: 'var(--surface-surface_0)',
  transparent: 'transparent',
} as const;

/**
 * Loading spinner component for button
 */
const LoadingSpinner: React.FC = () => (
  <svg
    style={{ 
      animation: 'spin 1s linear infinite',
      marginLeft: '-4px',
      marginRight: '8px',
      height: '16px',
      width: '16px'
    }}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      style={{ opacity: 0.25 }}
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      style={{ opacity: 0.75 }}
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/**
 * Button component following design system standards
 * 
 * @example
 * ```tsx
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Click me
 * </Button>
 * 
 * <Button variant="secondary" leftIcon={<Icon />} isLoading>
 *   Loading...
 * </Button>
 * ```
 */
const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  children,
  className,
  disabled = false,
  style,
  ...props
}) => {
  const isDisabled = disabled || isLoading;
  const [isHover, setIsHover] = React.useState(false);

  const handleMouseEnter = () => {
     setIsHover(true);
  };

  const handleMouseLeave = () => {
     setIsHover(false);
  };

  const getButtonStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 500,
      borderRadius: '12px',
      border: 'none',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      opacity: isDisabled ? 0.7 : 1,
      transition: 'all 0.2s ease-in-out',
      fontSize: '15px',
      fontFamily: 'inherit',
      textDecoration: 'none',
      userSelect: 'none',
      boxShadow: variant !== 'ghost' 
        ? 'var(--shadow-button)'
        : 'none',
    };

    const variantStyles: Record<NonNullable<ButtonProps['variant']>, React.CSSProperties> = {
      primary: {
        background: COLORS.primary,
        color: COLORS.white,
      },
      secondary: {
        background: isHover ? COLORS.greyBgHover : COLORS.greyBg,
        color: COLORS.greyText,
      },
      outline: {
        background: COLORS.white,
        color: COLORS.greyText,
        border: `1px solid ${COLORS.greyBg}`,
      },
      ghost: {
        background: COLORS.transparent,
        color: COLORS.greyText,
        boxShadow: 'none',
      },
      danger: {
        background: COLORS.danger,
        color: COLORS.white,
      },
    };

    const sizeStyles: Record<NonNullable<ButtonProps['size']>, React.CSSProperties> = {
      sm: {
        height: '36px',
        padding: '0 12px',
        fontSize: '13px',
        minWidth: '64px',
      },
      md: {
        height: '48px',
        padding: '0 16px',
        fontSize: '15px',
        minWidth: '80px',
      },
      lg: {
        height: '56px',
        padding: '0 24px',
        fontSize: '16px',
        minWidth: '96px',
      },
    };

    return {
      ...baseStyles,
      ...variantStyles[variant],
      ...sizeStyles[size],
      ...style,
    };
  };

  return (
    <button
      style={getButtonStyles()}
      disabled={isDisabled}
      className={className}
      aria-disabled={isDisabled}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {isLoading && <LoadingSpinner />}
      {!isLoading && leftIcon && (
        <span style={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
          {leftIcon}
        </span>
      )}
      <span>{children}</span>
      {!isLoading && rightIcon && (
        <span style={{ marginLeft: '8px', display: 'flex', alignItems: 'center' }}>
          {rightIcon}
        </span>
      )}
    </button>
  );
};

Button.displayName = 'Button';

export default Button;
